import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUserPlan } from '../hooks/useUserPlan';

const GlobalUpgradeBanner: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { planData, isPro } = useUserPlan();

  // Don't show banner if user is not logged in, is pro/premium, or we don't have plan data
  if (!currentUser || isPro || !planData) {
    return null;
  }

  const isLimitReached = planData.videos_count >= planData.videos_limit;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 animate-slide-up">
      <div className="bg-white border-t-2 border-primary-200 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Left section with icon and message */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex-shrink-0 w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                <Crown className="w-5 h-5 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-bold text-black mb-0.5 flex items-center gap-2">
                  {isLimitReached ? (
                    <>
                      You've Used Your Free Videos This Month!
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 inline text-red-600" />
                      {planData.videos_limit - planData.videos_count} Free Videos Remaining
                    </>
                  )}
                </h3>
                <p className="text-primary-600 text-sm hidden sm:block">
                  {isLimitReached 
                    ? "Upgrade now for unlimited videos, no watermarks, and 1080p quality"
                    : "Upgrade anytime for unlimited videos + no watermarks + 1080p quality"
                  }
                </p>
                <p className="text-primary-600 text-xs sm:hidden">
                  {isLimitReached 
                    ? "Upgrade for unlimited videos & more"
                    : "Upgrade for unlimited + premium features"
                  }
                </p>
              </div>
            </div>

            {/* Right section with stats and CTA */}
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              {/* Stats badges - hidden on mobile */}
              <div className="hidden lg:flex items-center gap-3">
                <div className="bg-primary-50 border border-primary-200 px-3 py-1.5 rounded-lg">
                  <span className="text-xs font-medium text-primary-600">
                    Videos: <span className={`font-semibold ${isLimitReached ? 'text-red-600' : 'text-black'}`}>{planData.videos_count || 0}/{planData.videos_limit}</span>
                  </span>
                </div>
                <div className="bg-primary-50 border border-primary-200 px-3 py-1.5 rounded-lg">
                  <span className="text-xs font-medium text-primary-600">
                    Quality: <span className="font-semibold text-black">720p</span>
                  </span>
                </div>
                <div className="bg-primary-50 border border-primary-200 px-3 py-1.5 rounded-lg">
                  <span className="text-xs font-medium text-primary-600">
                    Watermark: <span className="font-semibold text-black">Yes</span>
                  </span>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={() => navigate('/pricing')}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-2 whitespace-nowrap text-sm active:scale-[0.98]"
              >
                <Crown className="w-4 h-4" />
                <span className="hidden sm:inline">Upgrade to Pro</span>
                <span className="sm:hidden">Upgrade</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalUpgradeBanner;

